<template>
  <div class>
    <div class="px-2 my-4 flex justify-between">
      <div>
        <h1 class="text-4xl font-extrabold tracking-tight text-gray-900">Ma feuille de route</h1>
        <p class="text-sm text-gray-500 mt-1">
          {{ $d(new Date(), "shortDate") }}
        </p>
      </div>
      <Selector
        label="Sélectionner un magasin"
        v-if="showSelector"
        id="store-selector"
        name="store"
        :modelValue="selectedStoreLocalId"
        :options="storeOptions"
        @update:modelValue="onStoreChange"
      />
    </div>

    <div v-if="roadmapPerformance">
      <div class="flex justify-between items-center mb-4">
        <p class="text-sm text-gray-500">
          {{ quote }}
        </p>
        <div v-if="monthlySalesTarget && (isResponsible || hasAccessToAdminDashboard)">
          <SecondaryButton @click="editTargetModalOpen = true">Ajuster le potentiel de ventes mensuel</SecondaryButton>
          <Modal v-model="editTargetModalOpen" :open="editTargetModalOpen" @close="editTargetModalOpen = false">
            <StoreEditMonthlySalesTargetForm
              :store="selectedStoreWithMonthlySalesTarget"
              @updated="monthlyTargetUpdated"
            />
          </Modal>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <DailyTargetBlock :current="dailyTarget?.current" :target="dailyTarget?.target" />
        <YesterdayTargetBlock :current="yesterdayPerformance?.current" :target="yesterdayPerformance?.target" />
        <MonthlyTargetBlock
          :current="monthlyPerformance?.current"
          :target="monthlyPerformance?.target"
          :projection="monthlyPerformance?.projection"
        />
        <SellerRanking :sellerRanking="sellerRanking" />
        <StoreRanking :storeRanking="storeRanking" />
        <BulletPoints :bulletPoints="bulletPoints" />
      </div>
    </div>
    <Loading v-else-if="selectedStoreLocalId" />
  </div>
</template>

<script>
import DailyTargetBlock from "@/components/analytics/roadmap/DailyTargetBlock.vue";
import YesterdayTargetBlock from "@/components/analytics/roadmap/YesterdayTargetBlock.vue";
import MonthlyTargetBlock from "@/components/analytics/roadmap/MonthlyTargetBlock.vue";
import SellerRanking from "@/components/analytics/roadmap/SellerRanking.vue";
import StoreRanking from "@/components/analytics/roadmap/StoreRanking.vue";
import BulletPoints from "@/components/analytics/roadmap/BulletPoints.vue";
import StoreService from "@/api/services/store";
import StoreEditMonthlySalesTargetForm from "@/components/store/StoreEditMonthlySalesTargetForm.vue";
import { Selector, Loading, SecondaryButton, Modal } from "@estaly/ui";
import { toCamelCase } from "@estaly/ui/src/utils/data_formatter";
import { mapGetters } from "vuex";

export default {
  components: {
    DailyTargetBlock,
    YesterdayTargetBlock,
    MonthlyTargetBlock,
    Selector,
    Loading,
    SellerRanking,
    StoreRanking,
    BulletPoints,
    SecondaryButton,
    Modal,
    StoreEditMonthlySalesTargetForm,
  },
  data: () => {
    return {
      selectedStoreLocalId: null,
      roadmapPerformance: null,
      quote: null,
      dailyTarget: null,
      yesterdayPerformance: null,
      monthlyPerformance: null,
      sellerRanking: null,
      storeRanking: null,
      bulletPoints: null,
      editTargetModalOpen: false,
    };
  },
  computed: {
    ...mapGetters("store", ["selectedStoreId", "stores", "selectedStore"]),
    ...mapGetters("auth", ["hasAccessToAdminDashboard", "isResponsible"]),
    storeOptions() {
      return this.stores
        .filter((s) => s.source === "offline")
        .map((s) => ({
          value: s.id,
          label: s.salesChannel,
        }));
    },
    showSelector() {
      return this.storeOptions.length > 1 && (!this.selectedStoreId || this.selectedStoreId === 0);
    },
    monthlySalesTarget() {
      return this.monthlyPerformance?.target;
    },
    selectedStoreWithMonthlySalesTarget() {
      const store = this.stores.find((s) => s.id === this.selectedStoreLocalId);
      return {
        ...store,
        monthlySalesTarget: this.monthlySalesTarget || 0,
      };
    },
  },
  mounted() {
    if (!this.selectedStoreLocalId && this.storeOptions.length === 1) {
      this.selectedStoreLocalId = this.storeOptions[0].value;
      this.fetchRoadmapPerformance();
    } else if (!this.selectedStoreLocalId && this.selectedStoreId && this.selectedStoreId !== 0) {
      this.selectedStoreLocalId = this.selectedStoreId;
      this.fetchRoadmapPerformance();
    }
  },
  methods: {
    async fetchRoadmapPerformance() {
      const response = await StoreService.getRoadmapPerformance(this.selectedStoreLocalId);
      if (!response?.success) return;
      const data = toCamelCase(response.data);
      this.roadmapPerformance = data;
      this.quote = data.quote;
      this.dailyTarget = data.dailyTarget;
      this.yesterdayPerformance = data.yesterdayPerformance;
      this.monthlyPerformance = data.monthlyPerformance;
      this.sellerRanking = data.sellerRanking;
      this.storeRanking = data.storeRanking;
      this.bulletPoints = data.bulletPoints;
    },
    onStoreChange(storeId) {
      this.selectedStoreLocalId = storeId;
      this.fetchRoadmapPerformance();
    },
    async monthlyTargetUpdated() {
      this.editTargetModalOpen = false;
      await this.fetchRoadmapPerformance();
    },
  },
  watch: {
    storeOptions(newOptions) {
      if (newOptions.length === 1) {
        this.selectedStoreLocalId = newOptions[0].value;
        this.fetchRoadmapPerformance();
      }
    },
  },
};
</script>
